import {BoundingBoxClassControl} from '@wandb/weave/common/components/MediaCard';
import {DEFAULT_ALL_MASK_CONTROL} from '@wandb/weave/components/Panel2/ImageWithOverlays';
import Color from 'color';
import produce from 'immer';
import {get, setWith} from 'lodash';
import React, {useCallback} from 'react';

import {segmentationMaskColor} from '../../../../../../util/media';
import {OverlaysTable} from '../../common/OverlaysTable';
import {OverlaysTableRow} from '../../common/OverlaysTableRow';
import {useMediaSettingsDrawerContext} from '../../MediaSettingsDrawerContextProvider';
import {BoundingBoxStrokeTypeSelector} from './BoundingBoxStrokeTypeSelector';

export const BoundingBoxClasses = ({
  boxKey,
  mediaKey,
  boxQueryRegex,
}: {
  boxKey: string;
  mediaKey: string;
  boxQueryRegex: RegExp;
}) => {
  const {allBoxes, updateConfig, config, mediaCardTypes} =
    useMediaSettingsDrawerContext();
  const boundingBoxConfig = config.boundingBoxConfig ?? {};
  const classDict = allBoxes[mediaKey][boxKey].value;
  const allConfigPath = ['toggles', mediaKey, boxKey, 'all'];
  const allBoxControl: BoundingBoxClassControl =
    get(boundingBoxConfig, allConfigPath) ?? DEFAULT_ALL_MASK_CONTROL;

  const update = useCallback(
    (
      newControl: Partial<BoundingBoxClassControl>,
      configPath: Array<string>
    ) => {
      const boxControl = get(config.boundingBoxConfig, configPath);
      const newConfig = produce(config.boundingBoxConfig ?? {}, draft => {
        if (
          configPath[configPath.length - 1] === 'all' &&
          draft.toggles?.[mediaKey]?.[boxKey]
        ) {
          // clear all existing toggles. This is so that we will properly fall back to the
          // parent mask control below.
          delete draft.toggles[mediaKey][boxKey];
        }
        return setWith(
          draft,
          configPath,
          {...boxControl, ...newControl},
          Object
        );
      });
      updateConfig({boundingBoxConfig: newConfig});
    },
    [config.boundingBoxConfig, updateConfig, mediaKey, boxKey]
  );
  // This defines the hierarchy of bounding box controls. The child bounding box controls can override
  // the parent bounding box controls. This is so that the user can hide all bounding boxes, but then enable
  // a specific bounding box.
  const isAllBoundingBoxesDisabled = allBoxControl.disabled;
  const getIsBoundingBoxDisabled = (
    boxControl: BoundingBoxClassControl | undefined
  ) => boxControl?.disabled ?? isAllBoundingBoxesDisabled;

  return (
    <OverlaysTable
      isAllVisible={!allBoxControl.disabled}
      setIsAllVisible={() =>
        update({disabled: !allBoxControl.disabled}, allConfigPath)
      }
      title={boxKey}
      rightContent={
        // Only image media panels support stroke type selection for bounding boxes
        mediaCardTypes?.some(mediaCardType => mediaCardType === 'image') ? (
          <BoundingBoxStrokeTypeSelector
            mediaKey={mediaKey}
            boxKey={boxKey}
            boxConfig={boundingBoxConfig}
            updateConfig={updateConfig}
          />
        ) : undefined
      }>
      {Object.entries(classDict).reduce<JSX.Element[]>((acc, [k, name]) => {
        if (!name.match(boxQueryRegex)) {
          return acc; // Skip if it doesn't match
        }
        const configPath = ['toggles', mediaKey, boxKey, k];

        const boxControl: BoundingBoxClassControl = get(
          boundingBoxConfig,
          configPath
        );

        const onClickHandler = () =>
          update({disabled: !getIsBoundingBoxDisabled(boxControl)}, configPath);
        const setColor = (newColor: string) => {
          const c = Color(newColor);
          update({color: [c.red(), c.green(), c.blue()]}, configPath);
        };

        acc.push(
          <OverlaysTableRow
            data-test="bounding-box-class-row"
            key={mediaKey + boxKey + name}
            title={name}
            isVisible={!getIsBoundingBoxDisabled(boxControl)}
            setIsVisible={onClickHandler}
            setColor={
              mediaCardTypes?.some(mediaCardType => mediaCardType === 'image')
                ? setColor
                : undefined
            }
            color={
              // Bounding boxes can be customized for point clouds via SDK so they might be all
              // different colors even if they have the same label. You can't
              // edit it either via UI, so just hide it.
              mediaCardTypes?.some(mediaCardType => mediaCardType === 'image')
                ? (boxControl?.color ?? segmentationMaskColor(Number(k)))
                : undefined
            }
          />
        );

        return acc;
      }, [])}
    </OverlaysTable>
  );
};
